import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ssoConsumeToken, getMe } from '../store/authSlice';
import { DoubleRing } from '../components/motion/PageLoader';

/**
 * SSO Landing Page
 *
 * Consumes a JWT handed over from portal.sentrivox.com via the URL hash
 * (e.g. /sso#t=<jwt>). The token is moved into localStorage as
 * `cloudToken`, the hash is scrubbed, and getMe() is called to populate
 * the user. If the user lacks cloud access, they are bounced to /login
 * with an upgrade message.
 *
 * Hash is used (not query string) so the token is never sent to the
 * server, never logged in access logs, and not included in Referer.
 */
const SsoLanding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || '';
    // Accept either `#t=<jwt>` or `#token=<jwt>`
    const match = hash.match(/(?:^|[#&])(?:t|token)=([^&]+)/);
    const token = match ? decodeURIComponent(match[1]) : null;

    // Scrub the hash immediately so the token isn't visible in the URL bar / history
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch (_) { /* ignore */ }

    if (!token) {
      setError('Missing SSO token. Redirecting to login…');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
      return;
    }

    // Store token and prime redux state, then validate via /api/auth/me
    dispatch(ssoConsumeToken(token));

    dispatch(getMe())
      .unwrap()
      .then((data) => {
        const u = data?.user;
        if (!u?.hasCloudAccess) {
          // Plan doesn't include cloud — clear and bounce
          try {
            localStorage.removeItem('cloudToken');
            localStorage.removeItem('cloudUser');
          } catch (_) { /* ignore */ }
          navigate('/login?reason=upgrade-required', { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        try {
          localStorage.removeItem('cloudToken');
          localStorage.removeItem('cloudUser');
        } catch (_) { /* ignore */ }
        setError('Single sign-on failed. Please log in again.');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      });
  }, [dispatch, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif',
      color: '#1f2937'
    }}>
      {!error && <div style={{ marginBottom: 16 }}><DoubleRing /></div>}
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
        {error ? error : 'Signing you in…'}
      </div>
      <div style={{ fontSize: 13, color: '#6b7280' }}>
        Establishing secure session with Sentrivox Cloud Security.
      </div>
    </div>
  );
};

export default SsoLanding;
